/**
 * Labels for key caps. Bindings are physical keys, so on AZERTY the "W" key is labelled Z:
 * where the browser can tell (Keyboard Map API, Chromium), show the letter printed on the
 * player's key. Non-Latin layouts keep the Latin letter, which those keyboards also carry.
 * Named keys (Space, Esc, Tab) are translated by the HUD.
 */
import { useSyncExternalStore } from 'react';

interface LayoutMap {
  get(code: string): string | undefined;
}

interface KeyboardApi {
  getLayoutMap?: () => Promise<LayoutMap>;
}

let layout: LayoutMap | null = null;
let version = 0;
let requested = false;
const listeners = new Set<() => void>();

function request(): void {
  if (requested || typeof navigator === 'undefined') return;
  requested = true;
  const kb = (navigator as Navigator & { keyboard?: KeyboardApi }).keyboard;
  if (!kb?.getLayoutMap) return;
  kb.getLayoutMap()
    .then((map) => {
      layout = map;
      version++;
      for (const l of listeners) l();
    })
    .catch(() => {
      // Not allowed here (iframes, some privacy settings): keep the default labels.
    });
}

/** The label for a physical letter or digit key, e.g. KeyW → "W" (or "Z" on AZERTY). */
export function keyCapLabel(code: string): string {
  const printed = layout?.get(code);
  if (printed && /^[a-z]$/i.test(printed)) return printed.toUpperCase();
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  return code;
}

function subscribe(cb: () => void): () => void {
  request();
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Re-renders the caller once the keyboard layout is known. */
export function useKeyboardLayout(): number {
  return useSyncExternalStore(subscribe, () => version);
}
